import { ref } from 'vue';
import type { ApplicationDetail } from '@/entities/application/model/types';
import { useFetchHotels, type HotelSelectItem } from '@/features/application/applicationForm';
import { useFormValidation } from '@/shared/lib/validation';
import { useUpdateApplicationDetail } from './useUpdateApplicationDetail';
import { adminApplicationUpdateSchema } from './schema';
import { buildFormFromApplicationDetail } from '../lib/buildFormFromApplicationDetail';
import type { UpdateApplicationDetailRequest, RelationshipWithGuest } from './types';

/**
 * 申込編集機能を提供するカスタムフック
 *
 * @param getApplicationDetail 申込詳細データを取得するgetter関数
 */
export const useApplicationEdit = (getApplicationDetail: () => ApplicationDetail | undefined) => {
    // 編集モード状態
    const isEditMode = ref(false);
    const isLoading = ref(false);

    const hotelItems = ref<HotelSelectItem[]>([]);

    const {
        form,
        errors: validationErrors,
        validate,
    } = useFormValidation(adminApplicationUpdateSchema, buildFormFromApplicationDetail(getApplicationDetail()));

    const { initializeHotels } = useFetchHotels();
    const { update, error: updateError, isLoading: isUpdating, clearError } = useUpdateApplicationDetail();

    /**
     * 編集モードを開始する
     * ホテル一覧を取得し、フォームを初期化する
     */
    const startEdit = async (): Promise<boolean> => {
        const detail = getApplicationDetail();
        if (!detail) return false;

        isLoading.value = true;
        clearError();

        try {
            // ホテル一覧を取得
            hotelItems.value = await initializeHotels(detail.hotel_info.hotel_group_id);

            // フォームに現在のデータを設定
            form.value = buildFormFromApplicationDetail(detail);

            isEditMode.value = true;
            return true;
        } catch {
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * 編集モードをキャンセルする
     */
    const cancelEdit = () => {
        isEditMode.value = false;
        form.value = buildFormFromApplicationDetail(getApplicationDetail());
        // バリデーションエラーをクリア
        validationErrors.value = {};
        clearError();
    };

    /**
     * 申込情報を保存する
     *
     * @returns 成功時はtrue、失敗時はfalse
     */
    const save = async (): Promise<boolean> => {
        const detail = getApplicationDetail();
        if (!detail) return false;

        // バリデーション
        if (!validate()) return false;

        // APIリクエスト用にデータを変換
        const request: UpdateApplicationDetailRequest = {
            customer_info: {
                reservation_holder_name: form.value.reservationHolderName,
                user_name: form.value.userName,
                relationship_with_guest: form.value.relationshipWithGuest as RelationshipWithGuest,
                email: form.value.email,
                phone_number: form.value.phoneNumber || null,
                country_name: form.value.countryName || null,
            },
            hotel_info: {
                id: form.value.hotelId,
                check_in_date: form.value.checkInDate,
                check_out_date: form.value.checkOutDate,
            },
        };

        const success = await update(detail.customer_info.application_id, request);

        if (success) {
            isEditMode.value = false;
        }

        return success;
    };

    return {
        // 状態
        isEditMode,
        isLoading,
        isUpdating,
        hotelItems,
        form,
        validationErrors,
        updateError,

        // アクション
        startEdit,
        cancelEdit,
        save,
        clearError,
    };
};
